import { Home, Search, ChefHat, Calendar, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export function NotFound() {
  const navigate = useNavigate();
  const quickLinks = [
    { 
      icon: Calendar, 
      label: 'Meal Plan', 
      path: '/MealPlan',
      color: 'from-[#7B9EC6] to-[#5B7E9E]'
    },
    { 
      icon: ChefHat, 
      label: 'Recipes', 
      path: '/AllRecipes',
      color: 'from-[#EA8B6F] to-[#C85534]'
    },
    { 
      icon: ShoppingBag, 
      label: 'Shopping', 
      path: '/ShoppingList',
      color: 'from-[#8FAA96] to-[#6B8A76]'
    },
  ];

  return (
    <div className="min-h-screen bg-[#F8F7F5] flex flex-col">
      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full text-center">
          {/* 404 Illustration */}
          <div className="mb-8 relative">
            <div className="text-[120px] md:text-[180px] font-bold text-gray-200 leading-none select-none">
              404
            </div>
            
            {/* Floating food icons */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative w-full h-full">
                {/* Carrot icon - top left */}
                <div className="absolute top-4 left-1/4 animate-bounce" style={{ animationDelay: '0s', animationDuration: '3s' }}>
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-[#EA8B6F] rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl md:text-3xl">🥕</span>
                  </div>
                </div>
                
                {/* Avocado icon - top right */}
                <div className="absolute top-8 right-1/4 animate-bounce" style={{ animationDelay: '0.5s', animationDuration: '3.5s' }}>
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-[#8FAA96] rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl md:text-3xl">🥑</span>
                  </div>
                </div>
                
                {/* Tomato icon - bottom center */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-bounce" style={{ animationDelay: '1s', animationDuration: '4s' }}>
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-[#C85534] rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-2xl md:text-3xl">🍅</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Message */}
          <h1 className="text-3xl md:text-4xl mb-4">
            Oops! Recipe Not Found
          </h1>
          <p className="text-lg text-[#757575] mb-8 leading-relaxed">
            Looks like this page wandered off the menu. Don't worry, we've got plenty of other delicious options for you!
          </p>

          {/* Primary Action */}
          <button
            onClick={() => navigate('/MealPlan')}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#7B9EC6] to-[#5B7E9E] text-white rounded-full shadow-md hover:shadow-lg transition-all mb-8 group"
            aria-label="Go to home"
          >
            <Home className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Home</span>
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px bg-gray-300" />
            <span className="text-sm text-[#757575]">or explore</span>
            <div className="flex-1 h-px bg-gray-300" />
          </div>

          {/* Quick Links */}
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              return (
                <button
                  key={link.path}
                  onClick={() => navigate(link.path)}
                  className="group"
                  aria-label={`Go to ${link.label}`}
                >
                  <div className="bg-white rounded-2xl p-4 md:p-6 shadow-sm hover:shadow-md transition-all">
                    <div className={`w-12 h-12 md:w-14 md:h-14 mx-auto mb-3 bg-gradient-to-br ${link.color} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 md:w-7 md:h-7 text-white" />
                    </div>
                    <p className="text-sm md:text-base text-gray-900 font-medium">
                      {link.label}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Help Text */}
          <div className="mt-12 p-4 bg-white rounded-2xl border border-gray-200">
            <div className="flex items-start gap-3 text-left">
              <div className="w-8 h-8 bg-[#7B9EC6]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Search className="w-4 h-4 text-[#7B9EC6]" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900 mb-1">
                  Looking for something specific?
                </p>
                <p className="text-sm text-[#757575]">
                  Try searching for recipes or check your meal plan to find what you need.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 text-center">
        <p className="text-sm text-[#757575]">
          Need help? Contact us at{' '}
          <a 
            href="mailto:support@mealplanner.com" 
            className="text-[#7B9EC6] hover:text-[#5B7E9E] transition-colors underline"
          >
            support@mealplanner.com
          </a>
        </p>
      </div>
    </div>
  );
}

export default NotFound;
